import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, FormControl,
  InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar, Stack,
  Chip, Tooltip, LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { codeExtractionApi, statsApi } from '../../services/api';

// Free-text fields the brand code may be embedded in. Only needed for retailers whose
// feed does NOT state a supplier code: a stored raw_supplier_code wins over this config
// unconditionally, so setting it for a retailer that has one has no effect.
//
// 'raw_product_id' is deliberately NOT offered. Since the identity split it holds the
// retailer's INTERNAL code, so pointing extraction at it would mine identity for brand
// codes -- the exact confusion the split removed.
const FIELD_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'name', label: 'Raw Product Name' },
  { value: 'raw_description', label: 'Raw Description' },
  { value: 'description', label: 'Normalized Description' },
];

export default function RetailersTab() {
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Retailer Configuration
  const [configLoading, setConfigLoading] = useState(false);
  const [supplierCodeField, setSupplierCodeField] = useState('');
  const [supplierCodeFieldSecondary, setSupplierCodeFieldSecondary] = useState('');
  // Prefix strip (retailer_code_config): removed from the start of a STORED supplier code
  // before brand rules run. Konijnendijk: NUKnkf09114 -> nkf09114.
  const [stripPrefix, setStripPrefix] = useState('');
  const [stripPrefixWhy, setStripPrefixWhy] = useState('');
  const [stripLoaded, setStripLoaded] = useState('');
  const [stripPreview, setStripPreview] = useState(null);
  const [stripWarning, setStripWarning] = useState(null);
  const [reextractNote, setReextractNote] = useState(null);
  const [configDirty, setConfigDirty] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [unlockStatus, setUnlockStatus] = useState(null); // null | 'running' | 'done' | 'error'
  const [unlockProgress, setUnlockProgress] = useState('');
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    statsApi.getRetailers().then(setRetailers).catch(() => {});
  }, []);

  const loadCoverage = (retailerId) => {
    codeExtractionApi.getRetailerCoverage(retailerId).then(setCoverage).catch(() => setCoverage(null));
  };

  useEffect(() => {
    if (!selectedRetailer) return;
    setConfigLoading(true);
    setUnlockStatus(null);
    setUnlockProgress('');
    setCoverage(null);
    setStripPreview(null);
    setStripWarning(null);
    setReextractNote(null);
    loadCoverage(selectedRetailer);
    codeExtractionApi.getRetailerConfig(selectedRetailer)
      .then(data => {
        setSupplierCodeField(data.supplier_code_field || '');
        setSupplierCodeFieldSecondary(data.supplier_code_field_secondary || '');
        setStripPrefix(data.strip_prefix || '');
        setStripLoaded(data.strip_prefix || '');
        setStripPrefixWhy(data.strip_prefix_description || '');
        setConfigDirty(false);
      })
      .catch(() => {})
      .finally(() => setConfigLoading(false));
  }, [selectedRetailer]);

  // Unlock = one background job on the pipeline: rule discovery for brands
  // without an extraction rule (0-50%), network-wide apply of newly promoted
  // rules (50-70%), retailer re-extract (70-90%), Neo4j re-sync (90-100%).
  // Poll until it finishes — the old synchronous call hit the proxy timeout on
  // large retailers while the pipeline kept running.
  const unlockStageLabel = (pct) => {
    if (pct < 50) return 'Discovering brand rules…';
    if (pct < 70) return 'Applying new rules network-wide…';
    if (pct < 90) return 'Extracting codes…';
    return 'Syncing relationships…';
  };

  // A code-settings change starts the same job with discovery off, so it starts at 70%.
  const reextractStageLabel = (pct) => (pct < 90 ? 'Extracting codes…' : 'Syncing relationships…');

  // Poll a re-extract job until it finishes; shared by Unlock Matching and config saves.
  const followJob = async (jobId, stageLabel, what) => {
    setUnlockStatus('running');
    setUnlockProgress('Starting…');
    try {
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        const job = await codeExtractionApi.reinferStatus(jobId);
        if (job.progress_percent != null) {
          setUnlockProgress(stageLabel(job.progress_percent));
        }
        if (job.status === 'completed' || job.status === 'failed') {
          if (job.status === 'failed') {
            setUnlockStatus('error');
            setError(`${what} failed: ${job.error || 'unknown error'}`);
          } else {
            const ext = job?.result?.extraction || {};
            const disc = job?.result?.discovery;
            const discNote = disc
              ? ` — ${disc.promoted?.length ?? 0}/${disc.brands_discovered} new brand rules discovered`
              : '';
            setUnlockStatus('done');
            setSuccess(`${what} done — ${ext.products_processed ?? 0} products re-extracted, relationships synced${discNote}`);
          }
          loadCoverage(selectedRetailer);
          break;
        }
      }
    } catch (err) {
      setUnlockStatus('error');
      setError(err.message || `${what} failed`);
    } finally {
      setUnlockProgress('');
    }
  };

  const handleUnlockMatching = async () => {
    setUnlockStatus('running');
    setUnlockProgress('Starting…');
    try {
      const start = await codeExtractionApi.unlockMatching(selectedRetailer);
      const jobId = start?.job_id;
      if (!jobId) throw new Error(start?.error || 'No job_id returned');
      await followJob(jobId, unlockStageLabel, 'Unlock matching');
    } catch (err) {
      setUnlockStatus('error');
      setUnlockProgress('');
      setError(err.message || 'Unlock matching failed');
    }
  };

  const handleSaveConfig = async () => {
    const strip = stripPrefix.trim();
    if (strip && !stripPrefixWhy.trim()) {
      setError('Say why this prefix strip exists (it rewrites codes for the whole retailer).');
      return;
    }
    setConfigSaving(true);
    setStripPreview(null);
    setStripWarning(null);
    setReextractNote(null);
    try {
      const payload = {
        supplier_code_field: supplierCodeField || null,
        supplier_code_field_secondary: supplierCodeFieldSecondary || null,
      };
      // Only send the strip when it is set or being removed (PATCH semantics on the API).
      if (strip || stripLoaded) {
        payload.strip_prefix = strip;
        if (strip) payload.strip_prefix_description = stripPrefixWhy.trim();
      }
      const res = await codeExtractionApi.updateRetailerConfig(selectedRetailer, payload);
      setConfigDirty(false);
      setStripLoaded(strip);
      if (res?.strip_prefix_preview) setStripPreview(res.strip_prefix_preview);
      if (res?.warning) setStripWarning(res.warning);
      const re = res?.reextract;
      if (re?.started && re.job_id) {
        setSuccess('Configuration saved — re-extracting this retailer’s codes');
        followJob(re.job_id, reextractStageLabel, 'Re-extraction');
      } else if (re && !re.started) {
        setReextractNote(re.reason);
        setSuccess('Configuration saved');
      } else {
        setSuccess('Configuration saved');
      }
    } catch (err) { setError(err.message); }
    finally { setConfigSaving(false); }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Retailer Selector */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Select Retailer</InputLabel>
          <Select value={selectedRetailer} onChange={(e) => setSelectedRetailer(e.target.value)} label="Select Retailer">
            <MenuItem value="">-- Select a retailer --</MenuItem>
            {retailers.map(r => (
              <MenuItem key={r.id} value={r.id}>{r.name || r.id} ({r.product_count} products)</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {!selectedRetailer ? (
        <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 6 }}>
          Select a retailer above to configure code extraction.
        </Typography>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Configuration</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure which product fields contain supplier article numbers.
              The extraction regex is defined per brand on the Brands tab.
            </Typography>

            {configLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel shrink>Primary Supplier Code Field</InputLabel>
                    <Select
                      displayEmpty
                      value={supplierCodeField}
                      onChange={(e) => { setSupplierCodeField(e.target.value); setConfigDirty(true); }}
                      label="Primary Supplier Code Field"
                    >
                      {FIELD_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <InputLabel shrink>Secondary Field (fallback)</InputLabel>
                    <Select
                      displayEmpty
                      value={supplierCodeFieldSecondary}
                      onChange={(e) => { setSupplierCodeFieldSecondary(e.target.value); setConfigDirty(true); }}
                      label="Secondary Field (fallback)"
                    >
                      {FIELD_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Strip prefix (regex)"
                    value={stripPrefix}
                    onChange={(e) => { setStripPrefix(e.target.value); setConfigDirty(true); }}
                    placeholder="e.g. ^[A-Z]{3}"
                    sx={{ minWidth: 240 }}
                    helperText="Leave empty for none."
                  />
                  <TextField
                    size="small"
                    label="Why (required with a strip)"
                    value={stripPrefixWhy}
                    onChange={(e) => { setStripPrefixWhy(e.target.value); setConfigDirty(true); }}
                    placeholder="e.g. Retailer puts a 3-letter brand code in front of the supplier code"
                    sx={{ flex: 1 }}
                    disabled={!stripPrefix.trim()}
                  />
                  <Tooltip
                    title={
                      'Removed from the start of the supplier code stored from the feed, before the ' +
                      'brand rules run. Example: Konijnendijk writes NUKnkf09114 (NUK = its code for ' +
                      'nukus); with ^[A-Z]{3} the code read is nkf09114. Case-sensitive. Never applied to ' +
                      'product names or descriptions. Saving re-extracts this retailer’s codes.'
                    }
                  >
                    <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help', mt: 1 }} />
                  </Tooltip>
                </Stack>
                {stripWarning && <Alert severity="info">{stripWarning}</Alert>}
                {reextractNote && <Alert severity="warning">Saved, but re-extraction did not start: {reextractNote}</Alert>}
                {stripPreview && stripPreview.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Strip preview (sample of stored codes)</Typography>
                    {stripPreview.map((p, i) => (
                      <Typography key={i} variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {p.before} → {p.after}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained" size="small" startIcon={<SaveIcon />}
                    onClick={handleSaveConfig}
                    disabled={!configDirty || configSaving}
                  >
                    {configSaving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    onClick={handleUnlockMatching}
                    disabled={unlockStatus === 'running'}
                  >
                    {unlockStatus === 'running' ? (unlockProgress || 'Running…') : unlockStatus === 'done' ? 'Done ✓' : 'Unlock Matching'}
                  </Button>
                  <Tooltip
                    title={
                      'Runs the full chain as one background job: (1) AI rule discovery for this ' +
                      "retailer's brands that have no extraction rule yet (new rules are applied " +
                      'network-wide so partner products match too), (2) re-extracts supplier codes for ' +
                      'all of this retailer’s products, (3) re-syncs Neo4j relationships. ' +
                      'Discovered rules are only adopted if they pass the catalog-coverage gate; ' +
                      'gate-rejected brands stay listed in the coverage panel below and need a hand-set rule.'
                    }
                  >
                    <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
                  </Tooltip>
                  {unlockStatus === 'error' && (
                    <Typography variant="caption" color="error">Failed — check logs</Typography>
                  )}
                </Box>
                {unlockStatus === 'running' && <LinearProgress sx={{ maxWidth: 400 }} />}

                {coverage && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Extraction Coverage</Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        color={coverage.coverage_pct >= 90 ? 'success' : coverage.coverage_pct >= 60 ? 'warning' : 'error'}
                        label={`${coverage.products_with_code?.toLocaleString()} / ${coverage.products?.toLocaleString()} products have codes (${coverage.coverage_pct ?? 0}%)`}
                      />
                      {coverage.products_no_normalized_brand > 0 && (
                        <Chip
                          size="small" variant="outlined"
                          label={`${coverage.products_no_normalized_brand} products awaiting brand normalization`}
                        />
                      )}
                    </Stack>
                    {coverage.brands_without_rules?.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {coverage.brands_without_rules.length} brand{coverage.brands_without_rules.length !== 1 ? 's' : ''} without
                        an extraction rule ({coverage.brands_without_rules.reduce((s, b) => s + b.products, 0)} products) —
                        Unlock Matching will attempt AI rule discovery for these; brands that fail the coverage
                        gate need a hand-set rule on the <strong>Brands</strong> tab:{' '}
                        {coverage.brands_without_rules.slice(0, 8).map(b => b.normalized_brand).join(', ')}
                        {coverage.brands_without_rules.length > 8 ? ', …' : ''}
                      </Alert>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
